// tabs/DocumentTab.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Group, Text, SimpleGrid, Divider, ActionIcon, Tooltip, Flex
} from '@mantine/core';
import { IconTrash, IconEye, IconRefresh } from '@tabler/icons-react';
import { useACCourseService, CourseFileModelView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { FileUploader } from '@/components/ccms-components/file-management';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer';
import type { ViewerFile } from '@/components/ccms-components/file-management/types';
import { getFileIcon, formatUploadedDate } from '@components/ccms-components/utils/fileDisplay';
import ConfirmActionModal from './ConfirmActionModal';

// ─── Props ────────────────────────────────────────────────────────────────────
interface DocumentsTabProps {
  courseId: number;
  active: boolean;
  onCourseChange?: () => void;
}

// ─── Completed upload shape returned by FileUploader ──────────────────────────
interface CompletedUpload {
  url: string;
  fileName: string;
  mimeType?: string;
}

// ─── DocumentTab ──────────────────────────────────────────────────────────────
export default function DocumentTab({ courseId, active, onCourseChange }: DocumentsTabProps) {
  const courseService = useACCourseService();

  const [documents, setDocuments] = useState<CourseFileModelView[]>([]);
  const [loading, setLoading]     = useState(false);

  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction]   = useState<(() => Promise<void>) | null>(null);

  // ── Universal file viewer (preview) ────────────────────────────────────────
  const [viewerOpen, setViewerOpen]   = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  
  const viewerFiles: ViewerFile[] = useMemo(
  () =>
    documents.map((doc: any) => {
      const key = doc.fileID ?? doc.documentID ?? doc.courseFileID;
      return {
        id: String(key),
        fileName: doc.name ?? `File ${key}`,
        mimeType: doc.type ?? 'application/octet-stream',
        fileSize: 0,
        url: doc.link || undefined,   // empty string → undefined so `url &&` checks work
      };
    }),
  [documents]
);

  const openPreview = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  // ── Fetch files ────────────────────────────────────────────────────────────
  const fetchDocuments = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const result = await courseService.getFiles(courseId);
      setDocuments(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [courseId, courseService]);

  useEffect(() => {
    if (active) fetchDocuments();
  }, [active, fetchDocuments]);

  // ── Upload complete handler ────────────────────────────────────────────────
  const handleUploadComplete = async (files: CompletedUpload[]) => {
  try {
    await Promise.all(
      files.map((file) =>
        courseService.addFile(courseId, {
          courseId,
          name: file.fileName,
          link: file.url,   
          type: file.mimeType,
        } as any)
      )
    );
    await fetchDocuments();
    onCourseChange?.();
    toast.success(
      'File Added',
      `${files.length} file${files.length !== 1 ? 's' : ''} uploaded successfully.`
    );
  } catch (e) {
    console.error(e);
    toast.error('Error', 'File uploaded but failed to attach it to the course.');
  }
};

  // ── Remove file — opens confirm modal ─────────────────────────────────────
  const handleRemoveDocument = (doc: CourseFileModelView) => {
    const fileId = (doc as any).fileID ?? (doc as any).documentID ?? (doc as any).courseFileID;
    const name   = (doc as any).name ?? `File ${fileId}`;

    setConfirmMessage(`Remove "${name}" from this course?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await courseService.removeFile(courseId, fileId);
        await fetchDocuments();
        onCourseChange?.();
        toast.warning('File Removed', `"${name}" has been removed.`);
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove file.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
       <Flex gap='md' justify='flex-end' mb='md'>
         <Tooltip label="Refresh" withArrow>
                    <ActionIcon
                      variant="subtle"
                      onClick={() => fetchDocuments()}
                      loading={loading}
                      size="lg"
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
       </Flex>
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove File"
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      <UniversalFileViewer
        opened={viewerOpen}
        onClose={() => setViewerOpen(false)}
        files={viewerFiles}
        initialIndex={viewerIndex}
        onIndexChange={setViewerIndex}
      />

      <FileUploader
        domain="Course"
        recordId={String(courseId)}
        multiple
        accept={['*/*']}
        onUploadComplete={handleUploadComplete}
        onUploadError={(err: any) => {
          console.error(err);
          toast.error('Upload Failed', err?.message ?? 'Could not upload file.');
        }}
      />

      <Divider my="md" />

      {documents.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No files uploaded yet.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
         {documents.map((doc: any, index) => {
            const FileIcon = getFileIcon(doc.type);
            const key   = doc.fileID ?? doc.documentID ?? doc.courseFileID;
            const name  = doc.name ?? `File ${key}`;
            const ts    = doc.uploadedDateTime ?? doc.createdDateTime;
            const url   = doc.link || undefined;

            return (
              <Card key={key} withBorder radius="md" padding="lg">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ overflow: 'hidden' }}>
                    <FileIcon size={28} />
                    <div style={{ overflow: 'hidden' }}>
                      <Text fw={700} truncate>{name}</Text>
                      <Text size="xs" c="dimmed">
                        Uploaded: {formatUploadedDate(ts)}
                      </Text>
                    </div>
                  </Group>

                  <Group gap={4} wrap="nowrap">
                    {url && (
                      <Tooltip label="Preview">
                        <ActionIcon variant="light" color="blue" onClick={() => openPreview(index)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => handleRemoveDocument(doc)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            );
          })} 
        </SimpleGrid>
      )}
    </>
  );
}